import React from "react";
import { useSelector } from "react-redux";
import { Alert, Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import { AppState } from "../../store/root.reducer";
import User from "../../models/user.model";
import { ArtworkWorkspaceConfig } from "../../types/artworkWorkspace";
import { AdminEdmDatasetListItem } from "../../types/adminEdm";
import { WorkspaceStatusChip } from "./WorkspaceStatusChip";
import { getInstitutionsRequest } from "../../pages/admin/store/admin.api";
import {
  createEdmDatasetRequest,
  deleteEdmDatasetRequest,
  getEdmDatasetsRequest,
  updateEdmDatasetRequest,
} from "../../pages/admin/edm/store/admin-edm.api";
import {
  COMMON_LANGUAGE_TAG_OPTIONS,
  isValidLanguageTag,
  LANGUAGE_TAG_HELP_TEXT,
} from "../../pages/admin/edm/edm.helpers";
import adminTheme from "../../pages/admin/admin-theme.module.css";
import workspaceTheme from "./workspaceTheme.module.css";
import { getNormalizedUserRoles } from "../../utils/userAccess";

type Props = {
  config: ArtworkWorkspaceConfig;
};

type CreateDatasetState = {
  displayName: string;
  institutionId: string;
  defaultLanguage: string;
  summary: string;
  enabled: boolean;
};

const initialCreateState: CreateDatasetState = {
  displayName: "",
  institutionId: "",
  defaultLanguage: "en",
  summary: "",
  enabled: true,
};

const normalizeDatasets = (items: unknown): AdminEdmDatasetListItem[] =>
  Array.isArray(items) ? items as AdminEdmDatasetListItem[] : [];

export const DatasetsWorkspaceTab = ({ config }: Props) => {
  const navigate = useNavigate();
  const currentUser = useSelector((state: AppState) => state.auth.user as User | null);
  const userRoles = getNormalizedUserRoles(currentUser);
  const isCuratorDatasetUser = userRoles.includes("SELLER") && currentUser?.curator === true && Boolean(currentUser?.institutionId);

  const [datasets, setDatasets] = React.useState<AdminEdmDatasetListItem[]>(normalizeDatasets(config.datasets));
  const [loading, setLoading] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [enabledOnly, setEnabledOnly] = React.useState(false);
  const [institutionFilter, setInstitutionFilter] = React.useState("");
  const [showCreate, setShowCreate] = React.useState(false);
  const [createForm, setCreateForm] = React.useState<CreateDatasetState>({
    ...initialCreateState,
    institutionId: config.defaultInstitutionId || "",
  });
  const [institutions, setInstitutions] = React.useState<any[]>([]);
  const [institutionsLoading, setInstitutionsLoading] = React.useState(false);
  const [savingCreate, setSavingCreate] = React.useState(false);
  const [pendingDatasetKey, setPendingDatasetKey] = React.useState<string | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);

  const availableInstitutions = React.useMemo(
    () => institutions.filter((institution) => institution.active !== false),
    [institutions]
  );
  const createDefaultLanguageValid = isValidLanguageTag(createForm.defaultLanguage);

  const refreshDatasets = React.useCallback(async () => {
    setLoading(true);
    setActionError(null);
    const response = await getEdmDatasetsRequest({
      search: search.trim() || undefined,
      enabled: enabledOnly ? true : undefined,
      institutionId: !isCuratorDatasetUser ? institutionFilter || undefined : undefined,
    });

    if (response?.status) {
      setDatasets(normalizeDatasets(response.data?.items));
      setLoading(false);
      return;
    }

    setActionError(response?.error?.message || "Failed to load datasets.");
    setLoading(false);
  }, [enabledOnly, institutionFilter, isCuratorDatasetUser, search]);

  React.useEffect(() => {
    void refreshDatasets();
  }, [refreshDatasets]);

  React.useEffect(() => {
    if (isCuratorDatasetUser) {
      setInstitutions([]);
      return;
    }

    let cancelled = false;
    setInstitutionsLoading(true);
    getInstitutionsRequest()
      .then((response) => {
        if (cancelled) {
          return;
        }
        const items = response?.data?.data?.items;
        setInstitutions(Array.isArray(items) ? items : []);
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setInstitutions([]);
      })
      .finally(() => {
        if (!cancelled) {
          setInstitutionsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isCuratorDatasetUser]);

  const resetCreateForm = React.useCallback(() => {
    setCreateForm({
      ...initialCreateState,
      institutionId: config.defaultInstitutionId || "",
    });
  }, [config.defaultInstitutionId]);

  const onCreate = async () => {
    if (!createForm.displayName.trim() || !createDefaultLanguageValid) {
      return;
    }

    setSavingCreate(true);
    setActionError(null);
    const payload: Record<string, unknown> = {
      displayName: createForm.displayName.trim(),
      defaultLanguage: createForm.defaultLanguage.trim(),
      summary: createForm.summary.trim() || null,
    };

    if (!isCuratorDatasetUser) {
      payload.institutionId = createForm.institutionId || null;
      payload.enabled = createForm.institutionId ? createForm.enabled : false;
    }

    const response = await createEdmDatasetRequest(payload);
    setSavingCreate(false);

    if (!response?.status) {
      setActionError(response?.error?.message || "Dataset create failed.");
      return;
    }

    setShowCreate(false);
    resetCreateForm();
    await refreshDatasets();
    navigate(`/admin/edm/datasets/${encodeURIComponent(response.data?.datasetKey)}`);
  };

  const onToggleEnabled = async (datasetKey: string, enabled: boolean) => {
    setPendingDatasetKey(datasetKey);
    setActionError(null);
    const response = await updateEdmDatasetRequest(datasetKey, { enabled });
    setPendingDatasetKey(null);

    if (!response?.status) {
      setActionError(response?.error?.message || "Dataset update failed.");
      return;
    }

    await refreshDatasets();
  };

  const onDelete = async (dataset: AdminEdmDatasetListItem) => {
    const confirmed = window.confirm(
      `Delete dataset "${dataset.displayName || dataset.datasetKey}"? This is only allowed when no artworks are assigned.`
    );
    if (!confirmed) {
      return;
    }

    setPendingDatasetKey(dataset.datasetKey);
    setActionError(null);
    const response = await deleteEdmDatasetRequest(dataset.datasetKey);
    setPendingDatasetKey(null);

    if (!response?.status) {
      setActionError(response?.error?.message || "Dataset delete failed.");
      return;
    }

    await refreshDatasets();
  };

  return (
    <div className={`d-flex flex-column gap-3 ${workspaceTheme.sectionSurface}`}>
      {actionError ? (
        <Alert variant="warning" className={workspaceTheme.surfaceAlert}>
          {actionError}
        </Alert>
      ) : null}

      <div className={workspaceTheme.toolbar}>
        <div className={workspaceTheme.toolbarGroup}>
          <Form.Group style={{ minWidth: 280 }}>
            <Form.Label>Search datasets</Form.Label>
            <Form.Control
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search display name, key, institution"
            />
          </Form.Group>
          {!isCuratorDatasetUser ? (
            <Form.Group style={{ minWidth: 240 }}>
              <Form.Label>Institution</Form.Label>
              <Form.Select
                className={workspaceTheme.workspaceSelect}
                value={institutionFilter}
                disabled={institutionsLoading}
                onChange={(event) => setInstitutionFilter(event.target.value)}
              >
                <option value="">All institutions</option>
                <option value="__unassigned__">Unassigned</option>
                {availableInstitutions.map((institution) => (
                  <option key={institution._id} value={institution._id}>
                    {institution.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          ) : null}
          <Form.Group className="d-flex align-items-end">
            <Form.Check
              id="workspace-datasets-enabled-only"
              type="checkbox"
              label="Enabled only"
              checked={enabledOnly}
              onChange={(event) => setEnabledOnly(event.target.checked)}
            />
          </Form.Group>
        </div>
        <div className={workspaceTheme.toolbarActions}>
          <Button
            variant="light"
            className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary}`}
            onClick={() => void refreshDatasets()}
          >
            Refresh
          </Button>
          <Button
            variant="light"
            className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonPrimary}`}
            onClick={() => setShowCreate(true)}
          >
            Create Dataset
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-5 text-center">
          <Spinner animation="border" />
        </div>
      ) : datasets.length === 0 ? (
        <div className={workspaceTheme.emptyState}>No dataset profiles match the current filters.</div>
      ) : (
        <Table responsive hover variant="dark" className={workspaceTheme.darkTable}>
          <thead>
            <tr>
              <th>Dataset</th>
              <th>Institution</th>
              <th>Status</th>
              <th>Assigned</th>
              <th>Default Language</th>
              <th>Primary Profile</th>
              <th>Threshold</th>
              <th>Publishable A/1</th>
              <th>Blocked</th>
              <th>Updated</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {datasets.map((dataset) => {
              const assignedArtworkCount = dataset.assignedArtworkCount || 0;
              const canDelete = !isCuratorDatasetUser && assignedArtworkCount === 0;
              const canEnable = !isCuratorDatasetUser && Boolean(dataset.institutionId);
              const busy = pendingDatasetKey === dataset.datasetKey;
              return (
                <tr key={dataset.datasetKey}>
                  <td>
                    <div className={workspaceTheme.tableAction}>{dataset.displayName || dataset.datasetKey}</div>
                    <div className={workspaceTheme.secondaryText}>{dataset.datasetKey}</div>
                  </td>
                  <td>{dataset.institution?.name || "Unassigned"}</td>
                  <td>
                    <WorkspaceStatusChip
                      label={dataset.enabled ? "Enabled" : "Disabled"}
                      tone={dataset.enabled ? "success" : "neutral"}
                    />
                  </td>
                  <td>{assignedArtworkCount}</td>
                  <td>{dataset.defaultLanguage || "N/A"}</td>
                  <td>{dataset.primaryProfileId || "N/A"}</td>
                  <td>
                    {dataset.minimumMetadataTier && dataset.minimumContentTier != null
                      ? `${dataset.minimumMetadataTier}/${dataset.minimumContentTier}`
                      : "N/A"}
                  </td>
                  <td>{dataset.publishableA1}</td>
                  <td>{dataset.blockedCount}</td>
                  <td>{dataset.updatedAt ? new Date(dataset.updatedAt).toLocaleString() : "N/A"}</td>
                  <td className="text-end">
                    <div className="d-flex justify-content-end gap-2 flex-wrap">
                      {!isCuratorDatasetUser ? (
                        <>
                          <Button
                            variant="light"
                            size="sm"
                            className={`${workspaceTheme.workspaceButton} ${dataset.enabled ? workspaceTheme.workspaceButtonSecondary : workspaceTheme.workspaceButtonPrimary} ${workspaceTheme.workspaceButtonSmall}`}
                            disabled={busy || (!dataset.enabled && !canEnable)}
                            onClick={() => void onToggleEnabled(dataset.datasetKey, !dataset.enabled)}
                          >
                            {busy ? "Working..." : dataset.enabled ? "Disable" : "Enable"}
                          </Button>
                          <Button
                            variant="light"
                            size="sm"
                            className={`${workspaceTheme.workspaceButton} ${workspaceTheme.cardDangerAction} ${workspaceTheme.workspaceButtonSmall}`}
                            disabled={!canDelete || busy}
                            onClick={() => void onDelete(dataset)}
                          >
                            {busy ? "Deleting..." : "Delete"}
                          </Button>
                        </>
                      ) : null}
                      <Button
                        variant="light"
                        size="sm"
                        className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonPrimary} ${workspaceTheme.workspaceButtonSmall}`}
                        onClick={() => navigate(`/admin/edm/datasets/${encodeURIComponent(dataset.datasetKey)}`)}
                      >
                        Open
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      <Modal show={showCreate} onHide={() => setShowCreate(false)} centered className={adminTheme.darkModal}>
        <Modal.Header closeButton>
          <Modal.Title>Create Dataset</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Display name</Form.Label>
              <Form.Control
                value={createForm.displayName}
                onChange={(event) => setCreateForm((current) => ({ ...current, displayName: event.target.value }))}
                placeholder="Museum Test"
              />
            </Form.Group>
            {!isCuratorDatasetUser ? (
              <Form.Group className="mb-3">
                <Form.Label>Institution</Form.Label>
                <Form.Select
                  className={workspaceTheme.workspaceSelect}
                  value={createForm.institutionId}
                  disabled={institutionsLoading}
                  onChange={(event) => setCreateForm((current) => ({
                    ...current,
                    institutionId: event.target.value,
                    enabled: event.target.value ? current.enabled : false,
                  }))}
                >
                  <option value="">Unassigned</option>
                  {availableInstitutions.map((institution) => (
                    <option key={institution._id} value={institution._id}>
                      {institution.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            ) : (
              <Form.Group className="mb-3">
                <Form.Label>Institution</Form.Label>
                <Form.Control value={currentUser?.institution?.name || "Unavailable"} readOnly />
              </Form.Group>
            )}
            <Form.Group className="mb-3">
              <Form.Label htmlFor="create-dataset-default-language">Default language tag</Form.Label>
              <Form.Control
                id="create-dataset-default-language"
                list="create-dataset-language-tag-options"
                value={createForm.defaultLanguage}
                isInvalid={!createDefaultLanguageValid}
                placeholder="en"
                onChange={(event) => setCreateForm((current) => ({ ...current, defaultLanguage: event.target.value }))}
              />
              <datalist id="create-dataset-language-tag-options">
                {COMMON_LANGUAGE_TAG_OPTIONS.map((languageTag) => (
                  <option key={languageTag} value={languageTag} />
                ))}
              </datalist>
              <Form.Text className={adminTheme.darkModalMutedText}>{LANGUAGE_TAG_HELP_TEXT}</Form.Text>
              <Form.Control.Feedback type="invalid">
                Enter a valid BCP47 language tag such as en, ro, en-GB, or pt-BR.
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Summary</Form.Label>
              <Form.Control
                value={createForm.summary}
                onChange={(event) => setCreateForm((current) => ({ ...current, summary: event.target.value }))}
              />
            </Form.Group>
            {!isCuratorDatasetUser ? (
              <>
                <Form.Check
                  id="create-dataset-enabled"
                  type="checkbox"
                  label="Enabled"
                  checked={createForm.enabled}
                  disabled={!createForm.institutionId}
                  onChange={(event) => setCreateForm((current) => ({ ...current, enabled: event.target.checked }))}
                />
                {!createForm.institutionId ? (
                  <Form.Text className={adminTheme.darkModalMutedText}>
                    Unassigned datasets stay disabled until an institution is selected.
                  </Form.Text>
                ) : null}
              </>
            ) : null}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary}`}
            onClick={() => setShowCreate(false)}
          >
            Cancel
          </Button>
          <Button
            variant="light"
            className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonPrimary}`}
            onClick={() => void onCreate()}
            disabled={!createForm.displayName.trim() || savingCreate || !createDefaultLanguageValid}
          >
            {savingCreate ? "Creating..." : "Create Dataset"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
